/**
 * Per-thread query + read-state advance on mount.
 *
 * We mark-read once per mount when there's actually an unread tail. The
 * server is idempotent (it's a high-water mark, Section 3.2) so a duplicate
 * call is cheap but we avoid the noise anyway.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getThread, markThreadRead } from "../lib/api";
import { inboxKey, threadKey } from "./queryKeys";
import type { InboxResponse, ThreadResponse } from "../types/api";

export function useThread(counterparty: string): {
  data: ThreadResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const qc = useQueryClient();
  const q = useQuery<ThreadResponse, Error>({
    queryKey: threadKey(counterparty),
    queryFn: ({ signal }) => getThread(counterparty, { limit: 100 }, signal),
    staleTime: 2_500,
    // Polling replaces SSE while the thread is open and document is visible.
    refetchInterval: (q) => (document.visibilityState === "visible" && q.state.data ? 3_000 : false),
    refetchIntervalInBackground: false,
  });

  // Fire-and-forget mark-read once on first successful load.
  useEffect(() => {
    if (q.isSuccess && q.data && q.data.messages.length > 0) {
      // Messages are ordered DESC by lt — first row is the newest.
      const newestLt = q.data.messages[0]?.lt;
      if (newestLt === undefined) return;
      void markThreadRead(counterparty, newestLt).then(() => {
        qc.setQueryData<InboxResponse | undefined>(inboxKey(), (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            threads: prev.threads.map((t) =>
              t.counterparty_raw === counterparty || t.counterparty === counterparty
                ? { ...t, unread_count: 0 }
                : t,
            ),
          };
        });
      });
    }
    // We intentionally only re-run when the success state flips on, not on
    // every render. counterparty is stable per route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.isSuccess, counterparty]);

  return {
    data: q.data,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
